/** Guided Request Wizard — 5-step Who / What / Where / When / Review.
 *  Route: /services/request/:code
 *    - If :code is a bundle (starts with "BUN-" or the service fetch 404s),
 *      the wizard loads the bundle and prefills every included service as a
 *      line item.
 *    - Prefill via query params:
 *        ?employee=<id>  ?asset=<id>  ?site=<id>  ?bundle=<code>
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { rollupSLA } from "@/lib/slaRollup";
import {
  ArrowLeft, ArrowRight, Check, Loader2, CalendarDays, MapPin, User, Package,
  ClipboardCheck, Layers, Clock,
} from "lucide-react";

const STEPS = [
  { key: "who",    label: "Who",    icon: User },
  { key: "what",   label: "What",   icon: Package },
  { key: "where",  label: "Where",  icon: MapPin },
  { key: "when",   label: "When",   icon: CalendarDays },
  { key: "review", label: "Review", icon: ClipboardCheck },
];

const REQUEST_TYPE_MAP = {
  get_technology: "deploy_device",
  deploy: "deploy_device",
  move: "swap",
  support: "service",
  store: "other",
  recover: "reverse_pickup",
  reuse: "other",
  retire: "other",
  manage: "other",
  plan: "other",
  premium: "other",
};

export default function ServiceRequestWizard() {
  const { code } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();

  // A single item — either a bundle or a service — drives the wizard header
  // and the payload. Bundles unpack into `bundleServices` (locked line items).
  const [service, setService] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [bundleServices, setBundleServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masters, setMasters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assets, setAssets] = useState([]);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const [form, setForm] = useState({
    master_company_id: "",
    employee_id: sp.get("employee") || "",
    asset_ids: sp.get("asset") ? [sp.get("asset")] : [],
    address: "",
    country: "",
    needed_by: "",
    priority: "normal",
    notes: "",
  });

  // Load service OR bundle plus masters
  useEffect(() => {
    const isLikelyBundle = code?.startsWith("BUN-") || sp.get("type") === "bundle";
    const loadCatalogue = isLikelyBundle
      ? api.get(`/services/bundle/${code}`).then((r) => ({ kind: "bundle", data: r.data }))
      : api.get(`/services/${code}`).then((r) => ({ kind: "service", data: r.data }))
          .catch(() =>
            api.get(`/services/bundle/${code}`).then((r) => ({ kind: "bundle", data: r.data }))
          );
    Promise.all([loadCatalogue, api.get("/master-companies")])
      .then(([cat, mc]) => {
        setMasters(mc.data.items || []);
        if (cat.kind === "bundle") {
          setBundle(cat.data.bundle);
          setBundleServices(cat.data.services || []);
          // Use the bundle's group to drive request_type/needsAssets
          setService({
            code: cat.data.bundle.code,
            name: cat.data.bundle.name,
            customer_facing_name: cat.data.bundle.customer_facing_name,
            group: cat.data.bundle.group,
            sla_target: null,
          });
        } else {
          setService(cat.data.service);
        }
        if (mc.data.items?.length === 1) {
          setForm((f) => ({ ...f, master_company_id: mc.data.items[0].id }));
        }
      })
      .catch(() => { /* handled in the render below */ })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useEffect(() => {
    if (!form.master_company_id) { setEmployees([]); return; }
    api.get("/employees", { params: { master_company_id: form.master_company_id, limit: 300 } })
      .then(({ data }) => setEmployees(data.items || []));
  }, [form.master_company_id]);

  const needsAssets = useMemo(() => {
    if (!service) return false;
    return ["recover", "support", "reuse", "retire", "move"].includes(service.group);
  }, [service]);

  useEffect(() => {
    if (!needsAssets || !form.employee_id) { setAssets([]); return; }
    api.get(`/employees/${form.employee_id}`).then(({ data }) => setAssets(data.assets || []));
  }, [form.employee_id, needsAssets]);

  useEffect(() => {
    if (!form.employee_id || form.address) return;
    const e = employees.find((x) => x.id === form.employee_id);
    if (e?.delivery_address) setForm((f) => ({ ...f, address: e.delivery_address, country: e.country || f.country }));
  }, [form.employee_id, employees]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="max-w-3xl space-y-4"><div className="st-skeleton h-8 w-64" /><div className="st-skeleton h-64" /></div>;
  if (!service) return (
    <div className="max-w-3xl space-y-4">
      <Link to="/services" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"><ArrowLeft className="h-3.5 w-3.5" /> Back</Link>
      <div className="bg-white border border-slate-200 rounded-md p-6 text-sm text-slate-700">Service or bundle not found.</div>
    </div>
  );

  const canNext = () => {
    if (step === 0) return !!(form.master_company_id && form.employee_id);
    if (step === 1) return needsAssets ? (form.asset_ids.length > 0 || assets.length === 0) : true;
    if (step === 2) return !!form.address?.trim();
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const servicesSelected = bundle
        ? bundleServices.map((s) => s.code)
        : [service.code];
      const payload = {
        request_type: REQUEST_TYPE_MAP[service.group] || "other",
        service_id: bundle ? null : service.id,
        service_name: bundle
          ? `Bundle · ${bundle.customer_facing_name || bundle.name}`
          : (service.customer_facing_name || service.name),
        master_company_id: form.master_company_id,
        employee_id: form.employee_id || null,
        asset_ids: form.asset_ids,
        priority: form.priority,
        country: form.country || null,
        address: form.address || null,
        notes: [
          form.notes || "",
          bundle ? `\nBundle contents (${bundleServices.length}): ${bundleServices.map((s) => s.customer_facing_name || s.name).join(" · ")}` : "",
        ].filter(Boolean).join("\n").trim() || null,
        services_selected: servicesSelected,
      };
      const { data } = await api.post("/requests/", payload);
      setSubmitted(data);
      toast.success(`Request ${data.request_code || "created"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create request");
    } finally { setBusy(false); }
  };

  if (submitted) return (
    <div className="max-w-2xl space-y-4">
      <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="wizard-success">
        <div className="h-10 w-10 rounded-full bg-emerald-100 grid place-items-center mb-3">
          <Check className="h-5 w-5 text-emerald-700" strokeWidth={2} />
        </div>
        <h1 className="font-heading text-xl font-semibold text-slate-900">Request submitted</h1>
        <p className="text-sm text-slate-600 mt-1">
          {bundle ? `Bundle · ${bundle.customer_facing_name || bundle.name}` : (service.customer_facing_name || service.name)}
          {" · "}<span className="font-mono">{submitted.request_code || submitted.id?.slice(0, 8)}</span>
        </p>
        {bundle && (
          <div className="mt-3 text-xs text-slate-500">
            Captured {bundleServices.length} services: {bundleServices.map((s) => s.customer_facing_name || s.name).join(" · ")}
          </div>
        )}
        <div className="mt-5 flex gap-2">
          <Link to="/requests" className="h-10 px-4 rounded-md bg-slate-900 text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-slate-800">View my requests</Link>
          <Link to="/services" className="h-10 px-4 rounded-md border border-slate-200 text-sm font-medium inline-flex items-center gap-2 hover:bg-slate-50">Back to services</Link>
        </div>
      </div>
    </div>
  );

  const selectedEmp = employees.find((e) => e.id === form.employee_id);

  return (
    <div className="max-w-3xl space-y-6" data-testid="wizard-root">
      <Link
        to={bundle ? `/services/bundle/${code}` : `/services/detail/${code}`}
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
        data-testid="wizard-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to {bundle ? "bundle" : "service"}
      </Link>

      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
          {bundle ? "Request · Bundle" : "Request"}
        </div>
        <h1 className="mt-1 font-heading text-[24px] font-semibold text-slate-900 tracking-tight">
          {bundle ? (bundle.customer_facing_name || bundle.name) : (service.customer_facing_name || service.name)}
        </h1>
        {bundle && (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              <Layers className="h-3.5 w-3.5" strokeWidth={1.6} /> {bundleServices.length} services will be created as one outcome
            </span>
            {(() => {
              const roll = rollupSLA(bundleServices);
              return roll ? (
                <span className="inline-flex items-center gap-1" data-testid="wizard-bundle-sla">
                  <Clock className="h-3.5 w-3.5" strokeWidth={1.6} /> Overall target: <span className="font-medium text-slate-700">{roll.label}</span>
                </span>
              ) : null;
            })()}
          </div>
        )}
      </div>

      {/* Bundle contents panel (locked line items) */}
      {bundle && bundleServices.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md" data-testid="wizard-bundle-contents">
          <div className="px-4 py-3 border-b border-slate-100 text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
            Bundle contents
          </div>
          <ul className="divide-y divide-slate-100">
            {bundleServices.map((s, i) => (
              <li key={s.code} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`wizard-bundle-item-${s.code}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-[10px] font-mono text-slate-400 w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-slate-800 truncate">{s.customer_facing_name || s.name}</span>
                </div>
                <span className="text-[10px] text-slate-500 shrink-0">{s.sla_target || "—"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Stepper */}
      <ol className="flex items-center gap-2" aria-label="Progress">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = i < step;
          const active = i === step;
          return (
            <li key={s.key} className="flex items-center gap-2 flex-1">
              <div className={
                "h-8 w-8 rounded-full grid place-items-center border transition-colors " +
                (done ? "bg-slate-900 text-white border-slate-900" :
                  active ? "bg-white text-slate-900 border-slate-400 ring-2 ring-slate-900/10" :
                    "bg-white text-slate-400 border-slate-200")
              } data-testid={`wizard-step-${s.key}`}>
                {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" strokeWidth={1.6} />}
              </div>
              <div className={"text-xs font-medium " + (active ? "text-slate-900" : "text-slate-500")}>{s.label}</div>
              {i < STEPS.length - 1 && <div className={"flex-1 h-px " + (done ? "bg-slate-900" : "bg-slate-200")} />}
            </li>
          );
        })}
      </ol>

      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        {step === 0 && (
          <>
            <div>
              <label className="text-xs font-semibold text-slate-700">Customer</label>
              <select
                className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white"
                value={form.master_company_id}
                onChange={(e) => setForm({ ...form, master_company_id: e.target.value, employee_id: "" })}
                data-testid="wizard-customer"
              >
                <option value="">Select customer…</option>
                {masters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Employee</label>
              <select
                className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white disabled:opacity-50"
                value={form.employee_id}
                onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                disabled={!form.master_company_id}
                data-testid="wizard-employee"
              >
                <option value="">Select employee…</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.email}</option>)}
              </select>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div className="text-sm text-slate-700">
              {needsAssets ? (
                assets.length === 0 ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                    No assets linked to this employee. You can still submit — devices will be linked during fulfilment.
                  </div>
                ) : (
                  <div>
                    <div className="text-xs font-semibold text-slate-700 mb-2">Assets involved</div>
                    <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-64 overflow-y-auto">
                      {assets.map((a) => (
                        <label key={a.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50" data-testid={`wizard-asset-${a.id}`}>
                          <input
                            type="checkbox"
                            checked={form.asset_ids.includes(a.id)}
                            onChange={() => setForm((f) => ({
                              ...f, asset_ids: f.asset_ids.includes(a.id)
                                ? f.asset_ids.filter((x) => x !== a.id)
                                : [...f.asset_ids, a.id],
                            }))}
                          />
                          <div className="text-sm">
                            <div>{a.brand} {a.model}</div>
                            <div className="text-xs text-slate-500 font-mono">{a.serial}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )
              ) : (
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-700">
                  This {bundle ? "bundle" : "service"} doesn't need a specific device selection — Suntronix will source or pick the right ones during fulfilment.
                </div>
              )}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div>
              <label className="text-xs font-semibold text-slate-700">Delivery / pickup address</label>
              <textarea
                className="mt-1 w-full min-h-24 border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder={selectedEmp?.delivery_address || "Full address including PIN…"}
                data-testid="wizard-address"
              />
              {selectedEmp?.delivery_address && !form.address && (
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, address: selectedEmp.delivery_address, country: selectedEmp.country || f.country }))}
                  className="mt-1 text-[11px] text-slate-500 underline decoration-dotted hover:text-slate-900"
                >
                  Use employee's registered address
                </button>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Country</label>
              <input
                className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                placeholder="e.g. India"
                data-testid="wizard-country"
              />
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div>
              <label className="text-xs font-semibold text-slate-700">Needed by</label>
              <input
                type="date"
                className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white"
                value={form.needed_by}
                onChange={(e) => setForm({ ...form, needed_by: e.target.value })}
                data-testid="wizard-needed-by"
              />
              {service.sla_target && (
                <div className="mt-1 text-[11px] text-slate-500">Typical target for this service: <span className="font-medium text-slate-700">{service.sla_target}</span></div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Priority</label>
              <select
                className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                data-testid="wizard-priority"
              >
                <option value="normal">Normal</option>
                <option value="priority">Priority</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Notes (optional)</label>
              <textarea
                className="mt-1 w-full min-h-20 border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Anything Suntronix should know?"
                data-testid="wizard-notes"
              />
            </div>
          </>
        )}

        {step === 4 && (
          <div className="space-y-3">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div><dt className="text-xs text-slate-500 uppercase tracking-wide">{bundle ? "Bundle" : "Service"}</dt><dd className="text-slate-900 font-medium">{bundle ? (bundle.customer_facing_name || bundle.name) : (service.customer_facing_name || service.name)}</dd></div>
              {bundle && <div><dt className="text-xs text-slate-500 uppercase tracking-wide">Includes</dt><dd className="text-slate-900">{bundleServices.length} services</dd></div>}
              <div><dt className="text-xs text-slate-500 uppercase tracking-wide">Customer</dt><dd className="text-slate-900">{masters.find((m) => m.id === form.master_company_id)?.name || "—"}</dd></div>
              <div><dt className="text-xs text-slate-500 uppercase tracking-wide">Employee</dt><dd className="text-slate-900">{selectedEmp ? `${selectedEmp.name} · ${selectedEmp.email}` : "—"}</dd></div>
              <div><dt className="text-xs text-slate-500 uppercase tracking-wide">Assets</dt><dd className="text-slate-900">{form.asset_ids.length ? `${form.asset_ids.length} selected` : (needsAssets ? "None yet — will be linked" : "N/A")}</dd></div>
              <div className="col-span-2"><dt className="text-xs text-slate-500 uppercase tracking-wide">Address</dt><dd className="text-slate-900 whitespace-pre-wrap">{form.address || "—"}</dd></div>
              <div><dt className="text-xs text-slate-500 uppercase tracking-wide">Country</dt><dd className="text-slate-900">{form.country || "—"}</dd></div>
              <div><dt className="text-xs text-slate-500 uppercase tracking-wide">Priority</dt><dd className="text-slate-900 capitalize">{form.priority}</dd></div>
              <div><dt className="text-xs text-slate-500 uppercase tracking-wide">Needed by</dt><dd className="text-slate-900">{form.needed_by || "—"}</dd></div>
              {form.notes && <div className="col-span-2"><dt className="text-xs text-slate-500 uppercase tracking-wide">Notes</dt><dd className="text-slate-900 whitespace-pre-wrap">{form.notes}</dd></div>}
            </dl>
          </div>
        )}

        <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="h-10 px-4 rounded-md border border-slate-200 text-sm font-medium disabled:opacity-40 hover:bg-slate-50 inline-flex items-center gap-1.5"
            data-testid="wizard-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              disabled={!canNext()}
              className="h-10 px-4 rounded-md bg-slate-900 text-white text-sm font-medium disabled:opacity-40 hover:bg-slate-800 inline-flex items-center gap-1.5"
              data-testid="wizard-next-btn"
            >
              Next <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="h-10 px-4 rounded-md bg-slate-900 text-white text-sm font-medium disabled:opacity-40 hover:bg-slate-800 inline-flex items-center gap-1.5"
              data-testid="wizard-submit-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Submit request
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
